import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";
import { getConsent, saveConsent, applyConsent, CONSENT_EVENT } from "../lib/consent";
import CookieLicense from "./CookieLicense";

const categories = [
  { key: "necessary", title: "Nödvändiga", text: "Krävs för att webbplatsen ska fungera, t.ex. för att komma ihåg ditt cookieval. Kan inte stängas av.", locked: true },
  { key: "statistik", title: "Statistik", text: "Hjälper oss förstå hur webbplatsen används (anonymiserad besöksstatistik) så att vi kan förbättra den." },
];

export default function CookieConsent() {
  const [consent, setConsent] = useState(getConsent);
  const [settings, setSettings] = useState(false);
  const [draft, setDraft] = useState({ statistik: false });

  useEffect(() => {
    applyConsent(consent);
    const onChange = (e) => { setConsent(e.detail); setSettings(false); };
    const onOpen = () => { setDraft({ statistik: consent.statistik }); setSettings(true); };
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener("ct-open-cookie-settings", onOpen);
    return () => { window.removeEventListener(CONSENT_EVENT, onChange); window.removeEventListener("ct-open-cookie-settings", onOpen); };
  }, [consent]);

  if (consent.decided && !settings) return null;

  const acceptAll = () => saveConsent({ statistik: true });
  const necessaryOnly = () => saveConsent({ statistik: false });
  const saveDraft = () => saveConsent(draft);

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 cookie-enter" role="dialog" aria-modal="true" aria-labelledby="cookie-title" data-testid="cookie-banner">
      <div className="max-w-6xl mx-auto bg-[#0B2545]/95 backdrop-blur-md text-white rounded-2xl shadow-2xl shadow-slate-900/40 border border-white/10 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#ffce3a] via-[#ffd85f] to-[#ffce3a]" />
        <div className="px-5 py-4 md:px-6 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-[104px] h-[76px] sm:w-[124px] sm:h-[90px] flex-shrink-0 -my-3 -ml-1 cookie-float" data-testid="cookie-illustration-wrap">
              <CookieLicense className="w-full h-full drop-shadow-[0_8px_14px_rgba(0,0,0,0.35)]" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#ffce3a]">Nästan lika viktigt som körkortet</p>
              <h2 id="cookie-title" className="font-display text-base sm:text-lg font-bold leading-tight mt-0.5">Vi bjuder på kakor – vill du ha?</h2>
              <p className="text-[13px] text-slate-300 mt-1 leading-snug">
                Nödvändiga cookies håller sidan på vägen. Med ditt samtycke använder vi även anonym besöksstatistik för att bli ännu bättre.{" "}
                <Link to="/integritetspolicy" className="text-[#ffce3a] font-semibold hover:underline underline-offset-2" data-testid="cookie-policy-link">Läs vår policy</Link>
              </p>
            </div>
            {consent.decided && (
              <button onClick={() => setSettings(false)} aria-label="Stäng" className="lg:hidden ml-auto text-slate-400 hover:text-white" data-testid="cookie-close-btn"><X size={18} /></button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
            {settings ? (
              <button onClick={saveDraft} data-testid="cookie-save-settings" className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-[#ffce3a] text-[#0B2545] text-sm font-bold hover:bg-[#ffd85f] transition-colors">Spara mina val</button>
            ) : (
              <button onClick={() => { setDraft({ statistik: consent.statistik }); setSettings(true); }} data-testid="cookie-open-settings" className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-200 hover:text-white hover:bg-white/10 transition-colors">Anpassa</button>
            )}
            <button onClick={necessaryOnly} data-testid="cookie-necessary-only" className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-white/25 text-sm font-bold hover:bg-white/10 transition-colors">Endast nödvändiga</button>
            <button onClick={acceptAll} data-testid="cookie-accept-all" className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#ffce3a] text-[#0B2545] text-sm font-bold hover:bg-[#ffd85f] transition-colors shadow-lg shadow-[#ffce3a]/20">
              <ShieldCheck size={16} /> Acceptera alla
            </button>
            {consent.decided && (
              <button onClick={() => setSettings(false)} aria-label="Stäng" className="hidden lg:inline-flex text-slate-400 hover:text-white ml-1" data-testid="cookie-close-btn-desktop"><X size={18} /></button>
            )}
          </div>
        </div>

        {settings && (
          <div className="px-5 pb-5 md:px-6 grid sm:grid-cols-2 gap-3 border-t border-white/10 pt-4" data-testid="cookie-settings">
            {categories.map((c) => {
              const on = c.locked ? true : draft[c.key];
              return (
                <label key={c.key} className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${on ? "border-[#ffce3a]/50 bg-white/5" : "border-white/10"} ${c.locked ? "" : "cursor-pointer hover:bg-white/5"}`}>
                  <input type="checkbox" className="sr-only" checked={on} disabled={c.locked} onChange={(e) => setDraft({ ...draft, [c.key]: e.target.checked })} data-testid={`cookie-toggle-${c.key}`} />
                  <span className={`mt-0.5 w-10 h-[22px] rounded-full relative flex-shrink-0 transition-colors ${on ? "bg-[#ffce3a]" : "bg-white/20"} ${c.locked ? "opacity-70" : ""}`}>
                    <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[21px]" : "left-[3px]"}`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-sm">{c.title}{c.locked && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Alltid på</span>}</span>
                    <span className="block text-xs text-slate-300 mt-0.5 leading-snug">{c.text}</span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
